import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import './FaceSwapHistory.css';

interface HistoryItem {
  id: number;
  task_id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  filename: string;
  create_time: any | string | number;
  update_time: string;
  completed_time?: string;
  duration?: string;
  file_url?: string;
  thumbnail_url?: string;
  file_size?: number;
}

const FaceSwapHistory: React.FC = () => {
  const { user, isLoggedIn } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [allHistoryItems, setAllHistoryItems] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // 模拟数据
  const getMockData = (): HistoryItem[] => {
    const now = Date.now() / 1000; // 转换为秒级时间戳
    return [
      {
        id: 1,
        task_id: 'mock_task_001',
        status: 'completed',
        filename: 'faceswap_success.mp4',
        create_time: now - 3600,
        update_time: new Date(now * 1000).toISOString(),
        completed_time: new Date((now - 3500) * 1000).toISOString(),
        duration: '00:15',
        file_url: 'https://cdn.glistar.ai/mock/completed.mp4',
        thumbnail_url: 'https://cdn.glistar.ai/mock/completed_thumb.jpg',
        file_size: 5242880
      },
      {
        id: 2,
        task_id: 'mock_task_002',
        status: 'processing',
        filename: 'faceswap_processing.mp4',
        create_time: now - 300,
        update_time: new Date(now * 1000).toISOString()
      },
      {
        id: 3,
        task_id: 'mock_task_003',
        status: 'queued',
        filename: 'faceswap_queued.mp4',
        create_time: now - 60,
        update_time: new Date(now * 1000).toISOString()
      },
      {
        id: 4,
        task_id: 'mock_task_004',
        status: 'failed',
        filename: 'faceswap_failed.mp4',
        create_time: now - 7200,
        update_time: new Date(now * 1000).toISOString()
      }
    ];
  };

  // 获取用户历史记录
  const fetchUserHistory = async (page: number = 1, limit: number = 20) => {
    if (!isLoggedIn || !user) return { data: [], total: 0 };
    
    // 🐛 Debug mode: Scenario 3 - Force return empty data
    const debugEmptyHistory = localStorage.getItem('debug_empty_history');
    if (debugEmptyHistory === 'true') {
      console.log('🐛 Debug mode activated: Scenario 3 - Return empty history');
      return { data: [], total: 0 };
    }
    
    try {
      const token = localStorage.getItem('glistar_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/history?page=${page}&limit=${limit}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();
      if (result.status === 200) {
        // 如果API返回的数据为空，使用模拟数据
        const hasData = result.data.list && result.data.list.length > 0;
        return {
          data: hasData ? result.data.list : [],
          total: hasData ? result.data.total : 0,
          page: result.data.page || 1,
          limit: result.data.limit || 20
        };
      } else {
        console.error('Failed to fetch history:', response.statusText);
        // Return mock data when API fails
        return { data: [], total: 0 };
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      // Return mock data on network error
      return { data: [], total: 0 };
    }
  };

  // 删除任务
  const deleteTask = async (taskId: string) => {
    if (!isLoggedIn || !user) return false;
    
    try {
      const token = localStorage.getItem('glistar_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/del-history`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          // 在这里传你要的参数
          taskid: taskId
        })
      });
      const result = await response.json();
      if (result.status === 200) {
        // 从本地状态中移除已删除的任务
        setHistoryItems(prev => prev.filter(item => item.task_id !== taskId));
        setAllHistoryItems(prev => prev.filter(item => item.task_id !== taskId));
        setTotalCount(prev => prev - 1);
        return true;
      } else {
        console.error('Failed to delete task:', response.statusText);
        return false;
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      return false;
    }
  };

  // 下载任务文件
  const downloadTask = async (taskId: string) => {
    if (!isLoggedIn || !user) return;
    
    try {
      const token = localStorage.getItem('glistar_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/download-history`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          // 在这里传你要的参数
          taskid: taskId
        })
      });
      const result = await response.json();
      if (result.status === 200) {     
        // 创建下载链接
        const link = document.createElement('a');
        link.href = result.data.download_url;
        link.download = result.data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error('Failed to get download link:', response.statusText);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  // Initialize data loading
  useEffect(() => {
    const loadInitialData = async () => {
      console.log('🔍 FaceSwapHistory - Starting to load data');
      console.log('📊 Current status:', { isLoggedIn, hasUser: !!user, userEmail: user?.email });
      
      // 🐛 Debug mode: Scenario 4 - Delayed loading
      const debugDelay = localStorage.getItem('debug_delay_load');
      if (debugDelay) {
        const delay = parseInt(debugDelay);
        console.log(`🐛 Debug mode activated: Scenario 4 - Delay ${delay}ms to load data`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
      
      if (isLoggedIn && user) {
        console.log('✅ User logged in, fetching history...');
        const result = await fetchUserHistory(1, 4); // Show only 4 items on home page
        console.log('📦 API response:', { dataLength: result.data.length, total: result.total });
        setHistoryItems(result.data);
        setTotalCount(result.total);
        console.log('✅ History set:', { itemsCount: result.data.length, totalCount: result.total });
      } else {
        console.log('❌ User not logged in or user info is empty');
        console.log('   - isLoggedIn:', isLoggedIn);
        console.log('   - user:', user);
        setHistoryItems([]);
        setTotalCount(0);
      }
    };

    loadInitialData();
  }, [isLoggedIn, user]);

  // 初始化模态框数据
  const handleOpenModal = async () => {
    setIsModalOpen(true);
    if (allHistoryItems.length === 0) {
      const result = await fetchUserHistory(1, 20);
      setAllHistoryItems(result.data);
      setHasMore(result.data.length < result.total);
    }
  };

  // 加载更多数据
  const loadMore = async () => {
    if (isLoading || !hasMore) return;
    
    setIsLoading(true);
    try {
      const nextPage = Math.floor(allHistoryItems.length / 20) + 1;
      const result = await fetchUserHistory(nextPage, 20);
      
      if (result.data.length > 0) {
        setAllHistoryItems(prev => [...prev, ...result.data]);
        setHasMore(allHistoryItems.length + result.data.length < result.total);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to load more data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 处理滚动事件
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 10) {
      loadMore();
    }
  };

  // Don't show history area if user is not logged in or has no history
  console.log('🎯 FaceSwapHistory - Render check:', {
    isLoggedIn,
    hasUser: !!user,
    totalCount,
    willRender: isLoggedIn && !!user && totalCount > 0
  });
  
  if (!isLoggedIn || !user || totalCount === 0) {
    console.log('❌ FaceSwapHistory - Not rendering, reason:');
    if (!isLoggedIn) console.log('   - User not logged in (isLoggedIn = false)');
    if (!user) console.log('   - User info is empty (user = null)');
    if (totalCount === 0) console.log('   - History count is 0 (totalCount = 0)');
    return null;
  }
  
  console.log('✅ FaceSwapHistory - Rendering history area');

  return (
    <section className="fshistory-face-swap-history">
      <div className="fshistory-history-container">
        <div className="fshistory-history-header">
          <div className="fshistory-history-title-section">
            <h2 className="fshistory-history-title">History</h2>
            <div className="fshistory-history-count">
              <span className="fshistory-count-number">{totalCount}</span>
            </div>
          </div>
          {totalCount > 4 && (
            <button 
              className="fshistory-more-button"
              onClick={handleOpenModal}
            >
              More
            </button>
          )}
        </div>
        
        <div className="fshistory-history-grid">
          {historyItems.map((item) => (
            <div key={item.task_id} className={`fshistory-history-item fshistory-${item.status}`}>
              <div className="fshistory-history-card">
                <div className="fshistory-card-image">
                  {/* 显示缩略图或占位符 */}
                  {item.thumbnail_url && item.status === 'completed' ? (
                    <img 
                      src={item.thumbnail_url || item.file_url} 
                      alt={item.filename}
                      className="fshistory-thumbnail-image"
                      onError={(e) => {
                        // 图片加载失败时显示占位符
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : null}
                  <div className="fshistory-image-placeholder"></div>
                  
                  {/* 状态标签和遮罩层 */}
                  {item.status === 'queued' && (
                    <div className="fshistory-image-overlay">
                      <div className="fshistory-queued-badge">
                        <span>Queued</span>
                      </div>
                    </div>
                  )}
                  
                  {item.status === 'processing' && (
                    <div className="fshistory-image-overlay">
                      <div className="fshistory-processing-badge">
                        <span>Processing</span>
                      </div>
                    </div>
                  )}
                  
                  {item.status === 'failed' && (
                    <div className="fshistory-image-overlay">
                      <div className="fshistory-failed-badge">
                        <span>Failed</span>
                      </div>
                    </div>
                  )}
                  
                  {/* 时长标签 - 仅在完成状态显示 */}
                  {item.status === 'completed' && item.duration && (
                    <div className="fshistory-duration-badge">
                      <span>{item.duration}</span>
                    </div>
                  )}
                </div>
                
                <div className="fshistory-card-content">
                  <div className="fshistory-card-info">
                    <p className="fshistory-filename">{item.filename}</p>
                    <p className="fshistory-timestamp">{new Date(item.create_time * 1000).toLocaleString('zh-CN')}</p>
                  </div>
                  
                  {item.status === 'completed' && (
                    <div className="fshistory-action-buttons">
                      <button 
                        className="fshistory-action-btn fshistory-download-btn" 
                        title="Download"
                        onClick={() => downloadTask(item.task_id)}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M12 16L7 11L8.4 9.6L11 12.2V4H13V12.2L15.6 9.6L17 11L12 16Z" fill="currentColor"/>
                          <path d="M5 20V18H19V20H5Z" fill="currentColor"/>
                        </svg>
                      </button>
                      <button 
                        className="fshistory-action-btn fshistory-delete-btn" 
                        title="Delete"
                        onClick={() => deleteTask(item.task_id)}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M7 21Q6.45 21 6.225 20.775Q6 20.55 6 20V7H5V5H9V4H15V5H19V7H18V20Q18 20.55 17.775 20.775Q17.55 21 17 21H7ZM8 18H10V9H8V18ZM14 18H16V9H14V18Z" fill="currentColor"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Modal Dialog - 使用Portal渲染到body */}
      {isModalOpen && createPortal(
        <div className="fshistory-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="fshistory-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="fshistory-modal-header">
              <h3 className="fshistory-modal-title">All History</h3>
              <button 
                className="fshistory-modal-close-button"
                onClick={() => setIsModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="fshistory-modal-body" onScroll={handleScroll}>
              <div className="fshistory-modal-history-grid">
                {allHistoryItems.map((item) => (
                  <div key={item.task_id} className={`fshistory-modal-history-item fshistory-${item.status}`}>
                    <div className="fshistory-modal-history-card">
                      <div className="fshistory-modal-card-image">
                        {/* 显示缩略图或占位符 */}
                        {item.thumbnail_url && item.status === 'completed' ? (
                          <img 
                            src={item.thumbnail_url} 
                            alt={item.filename}
                            className="fshistory-modal-thumbnail-image"
                            onError={(e) => {
                              // 图片加载失败时显示占位符
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : null}
                        <div className="fshistory-modal-image-placeholder"></div>
                        
                        {/* 状态标签和遮罩层 */}
                        {item.status === 'queued' && (
                          <div className="fshistory-modal-image-overlay">
                            <div className="fshistory-modal-queued-badge">
                              <span>Queued</span>
                            </div>
                          </div>
                        )}
                        
                        {item.status === 'processing' && (
                          <div className="fshistory-modal-image-overlay">
                            <div className="fshistory-modal-processing-badge">
                              <span>Processing</span>
                            </div>
                          </div>
                        )}
                        
                        {item.status === 'failed' && (
                          <div className="fshistory-modal-image-overlay">
                            <div className="fshistory-modal-failed-badge">
                              <span>Failed</span>
                            </div>
                          </div>
                        )}
                        
                        {/* 时长标签 - 仅在完成状态显示 */}
                        {item.status === 'completed' && item.duration && (
                          <div className="fshistory-modal-duration-badge">
                            <span>{item.duration}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="fshistory-modal-card-content">
                        <div className="fshistory-modal-card-info">
                          <p className="fshistory-modal-filename">{item.filename}</p>
                          <p className="fshistory-modal-timestamp">{new Date(item.create_time * 1000).toLocaleString('zh-CN')}</p>
                        </div>
                        
                        {item.status === 'completed' && (
                          <div className="fshistory-modal-action-buttons">
                            <button 
                              className="fshistory-modal-action-btn-small fshistory-download-btn" 
                              title="Download"
                              onClick={() => downloadTask(item.task_id)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M12 16L7 11L8.4 9.6L11 12.2V4H13V12.2L15.6 9.6L17 11L12 16Z" fill="currentColor"/>
                                <path d="M5 20V18H19V20H5Z" fill="currentColor"/>
                              </svg>
                            </button>
                            <button 
                              className="fshistory-modal-action-btn-small fshistory-delete-btn" 
                              title="Delete"
                              onClick={() => deleteTask(item.task_id)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M7 21Q6.45 21 6.225 20.775Q6 20.55 6 20V7H5V5H9V4H15V5H19V7H18V20Q18 20.55 17.775 20.775Q17.55 21 17 21H7ZM8 18H10V9H8V18ZM14 18H16V9H14V18Z" fill="currentColor"/>
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* 加载状态 */}
              {isLoading && (
                <div className="fshistory-loading-indicator">
                  <div className="fshistory-loading-spinner"></div>
                  <span>Loading more...</span>
                </div>
              )}
              
              {/* 到底提示 */}
              {!hasMore && !isLoading && (
                <div className="fshistory-end-indicator">
                  <span>— No more history —</span>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

export default FaceSwapHistory;